import React, {
  FC,
  useRef,
  useState,
  useEffect,
  Dispatch,
  SetStateAction,
} from "react";
import {
  Box,
  VStack,
  HStack,
  Text,
  Button,
  Divider,
  Pressable,
  Image,
  Tooltip,
} from "native-base";
import { useWindowDimension } from "../../../Utility/WindowDimensions";
import { useSelector, RootStateOrAny, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { CalendarIcon } from "@mui/x-date-pickers";
import helping from "../../../assets/images/helping.png";
import inform from "../../../assets/images/inform.png";
import { Loc } from "../../../App/Loc";
import * as AV from "../../../AVCore/AVCore";
import { AddIcon, TeamBackground } from "../../../assets/icons/icon-assets";
import AVComDropdonwn from "../../common/AVComDropdown";
import AVCreateFeeCollection from "../../../components/common/AVCreateFeeCollection";

/* ----------------------------------
   Types
---------------------------------- */

type ExtendedEntity = AV.IAVEntity | AV.IEvent | AV.ICollect | AV.IOrgBase;

interface Props {
  entity: ExtendedEntity;
  setEntity: Dispatch<SetStateAction<ExtendedEntity | undefined>>;
  seriesId: string;
  isAdminView: boolean;
  isOngoingOrFuture: boolean;
}

/* ----------------------------------
   Helpers
---------------------------------- */

const getDaysLeft = (dateStr: string): string => {
  const targetDate = new Date(dateStr);
  const today = new Date();

  // Reset time for accurate day comparison
  today.setHours(0, 0, 0, 0);
  targetDate.setHours(0, 0, 0, 0);

  const diffTime = targetDate.getTime() - today.getTime();
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

  if (diffDays < 0) return Loc.currentLang.feeCollect.expired;
  if (diffDays === 0) return Loc.currentLang.feeCollect.today;

  return `${diffDays} ${diffDays > 1 ? Loc.currentLang.feeCollect.daysLeft : Loc.currentLang.feeCollect.dayLeft}`;
};

const parseCollectData = (data?: string | null) => {
  try {
    return data ? JSON.parse(data) : null;
  } catch {
    return null;
  }
};

/* ----------------------------------
   Component
---------------------------------- */

export const AVFeeCollection: FC<Props> = ({
  entity,
  setEntity,
  seriesId,
  isAdminView,
  isOngoingOrFuture,
}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const dimensions = useWindowDimension();
  const isMobile = dimensions?.width < 600;

  const currentUser = useSelector(
    (state: RootStateOrAny) => state?.user?.user?.[0] ?? null,
  );

  const isOnline = useSelector((state: RootStateOrAny) => state.user.isOnline);

  const [feeCollectionList, setFeeCollectionList] = useState<AV.ILink[]>([]);
  const [filterCollectData, setFilterCollectData] = useState<AV.ILink[]>([]);
  const [showCreateFeeCollection, setShowCreateFeeCollection] = useState(false);

  const [showSeriesEventOption, setShowSeriesEventOption] = useState(false);
  const [selectedSeriesEventOption, setSelectedSeriesEventOption] =
    useState("");

  const filterByType = (type: "Current" | "All" | "Past") => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    let collectList =
      feeCollectionList.length > 0
        ? feeCollectionList
        : (entity as AV.IOrgBase)?.childOrgs?.filter(
            (x) => x?.type === "Collect" && x?.subType !== "Channel",
          ) || [];

    //if (!isAdminView) {
    //  const userCollects =
    //    currentUser?.orgs?.filter(
    //      (x) => x?.type === "Collect" && x?.subType !== "Channel",
    //    ) ?? [];

    //  collectList = collectList.filter((collect) =>
    //    userCollects.some((uc) => uc.id === collect.id),
    //  );
    //}

    if (type === "All") {
      setFilterCollectData(collectList);
      setSelectedDropValue(Loc.currentLang.feeCollect.all);
      return;
    }

    const filtered = collectList.filter((item) => {
      const data = parseCollectData(item.data);
      if (!data?.end) return false;

      const endDate = new Date(data.end);
      if (isNaN(endDate.getTime())) return false;
      endDate.setHours(0, 0, 0, 0);

      if (type === "Current") {
        // today or future
        return endDate >= today;
      }

      if (type === "Past") {
        // before today
        return endDate < today;
      }

      return false;
    });

    setFilterCollectData(filtered);
    setSelectedDropValue(
      type === "Current"
        ? Loc.currentLang.feeCollect.current
        : Loc.currentLang.feeCollect.past,
    );
  };

  const [selectedDropValue, setSelectedDropValue] = useState(
    Loc.currentLang.feeCollect.current,
  );
  const dropdownFilterValues = [
    {
      id: 1,
      name: Loc.currentLang.feeCollect.current,
      flag: [],
      fn: () => filterByType("Current"),
    },
    {
      id: 2,
      name: Loc.currentLang.feeCollect.all,
      flag: [],
      fn: () => filterByType("All"),
    },
    {
      id: 3,
      name: Loc.currentLang.feeCollect.past,
      flag: [],
      fn: () => filterByType("Past"),
    },
  ];

  useEffect(() => {
    let collectList =
      (entity as AV.IOrgBase).childOrgs?.filter(
        (x) => x?.type === "Collect" && x?.subType !== "Channel",
      ) || [];

    //// Member view: show only collects the user belongs to
    //if (!isAdminView) {
    //  const userCollects =
    //    currentUser?.orgs?.filter(
    //      (x) => x?.type === "Collect" && x?.subType !== "Channel",
    //    ) ?? [];

    //  collectList = collectList.filter((collect) =>
    //    userCollects.some((uc) => uc.id === collect.id),
    //  );
    //}

    setFeeCollectionList(collectList);
    if (collectList?.length > 0) {
      setFilterCollectData(collectList);
      filterByType("Current");
    }
  }, [entity]);

  const menuRefs = useRef(null);
  useOutsideAlerter(menuRefs);
  function useOutsideAlerter(ref) {
    useEffect(() => {
      function handleClickOutside(event) {
        if (ref.current && !ref.current.contains(event.target)) {
          setShowSeriesEventOption(false);
        }
      }
      document.addEventListener("mousedown", handleClickOutside);
      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
      };
    }, [ref]);
  }

  return (
    <>
      <Box
        minHeight="340px"
        bg="AVColor.white"
        borderRadius={5}
        shadow={1}
        mb={3}
      >
        {/* Header */}
        <HStack
          justifyContent="space-between"
          alignItems="center"
          px={4}
          py={3}
        >
          <Text fontSize="22px" fontWeight="500" color="AVColor.primary">
            {Loc.currentLang.feeCollect.feeCollections}
          </Text>

          <HStack alignItems="center" space={3} zIndex={-3}>
            <Tooltip
              label={Loc.currentLang.feeCollect.helpText}
              openDelay={500}
              w={200}
            >
              <Box
                p="9px"
                shadow={1}
                bg="AVColor.liteBg"
                rounded="full"
                display={feeCollectionList.length ? "flex" : "none"}
              >
                <img src={helping} width="17" height="17" />
              </Box>
            </Tooltip>

            <AVComDropdonwn
              dropdownValues={dropdownFilterValues}
              bgColor="#00A7BC"
              text={selectedDropValue}
            />

            {isAdminView &&
              ((entity?.type !== "Event" && entity?.type !== "Game") ||
                isOngoingOrFuture) && (
                <Button
                  isDisabled={!isOnline}
                  p="10px"
                  shadow={1}
                  bg="gray.100"
                  _hover={{ bgColor: "gray.50" }}
                  rounded="full"
                  onPress={() => {
                    //if (seriesId) {
                    //  setShowSeriesEventOption(!showSeriesEventOption);
                    //} else {
                    setShowCreateFeeCollection(!showCreateFeeCollection);
                    //}
                  }}
                >
                  <AddIcon size="20px" color="#32AE78" />
                </Button>
              )}

            {showSeriesEventOption && (
              <VStack
                ref={menuRefs}
                width="125px"
                height="auto"
                shadow={2}
                background="#FFFFFF"
                position="absolute"
                top="50px"
                right="0"
                zIndex={999}
                borderRadius="6px"
              >
                <Pressable
                  width="100%"
                  height="36px"
                  px="10px"
                  justifyContent="center"
                  _hover={{ backgroundColor: "gray.100" }}
                  onPress={() => {
                    setShowSeriesEventOption(false);
                    setShowCreateFeeCollection(true);
                  }}
                >
                  <Text fontSize="14px" fontFamily="Roboto">
                    {Loc.currentLang.menu.thisOccurrence}
                  </Text>
                </Pressable>

                <Divider />

                <Pressable
                  width="100%"
                  height="36px"
                  px="10px"
                  justifyContent="center"
                  _hover={{ backgroundColor: "gray.100" }}
                  onPress={() => {
                    setShowSeriesEventOption(false);
                    setShowCreateFeeCollection(true);
                  }}
                >
                  <Text fontSize="14px" fontFamily="Roboto">
                    {Loc.currentLang.menu.entireSeries}
                  </Text>
                </Pressable>
              </VStack>
            )}
          </HStack>
        </HStack>

        <Divider my={2} zIndex={-3} />

        {/* List */}
        <HStack
          flexWrap="wrap"
          alignItems="flex-start"
          space={3}
          p={6}
          zIndex={-3}
        >
          {feeCollectionList.length === 0 ? (
            <HStack alignItems="center" space={2} w="100%">
              <img src={inform} width="20" height="20" />
              <Text fontSize="16px" fontWeight="400">
                {Loc.currentLang.feeCollect.helpText}
              </Text>
            </HStack>
          ) : filterCollectData.length === 0 ? (
            <Box py={4} alignItems="center" w="100%">
              <Text color="coolGray.400" fontSize="16px">
                {Loc.currentLang.feeCollect.noFeeCollectFound}
              </Text>
            </Box>
          ) : (
            filterCollectData.map((item) => {
              const data = parseCollectData(item.data);
              if (!data) return null;

              return (
                <Pressable
                  key={item.id}
                  w={{ base: "100%", md: "48%" }}
                  mb={5}
                  onPress={() =>
                    navigate(`/teams/${item.id}/${item.region}`, {
                      state: { isCreate: false, team: item },
                    })
                  }
                >
                  <Box
                    bg="white"
                    rounded="2xl"
                    shadow={3}
                    overflow="hidden"
                    h="390px"
                  >
                    {item?.background ? (
                      <Image
                        source={{ uri: AV.Media.getBackground(item) }}
                        alt="fee-image"
                        w="100%"
                        h="232px"
                      />
                    ) : (
                      <TeamBackground />
                    )}

                    <VStack space={2} p={4}>
                      <Text fontSize="lg" fontWeight="700">
                        {item.title}
                      </Text>

                      <Text fontSize="sm" color="gray.600" numberOfLines={2}>
                        {data.text}
                      </Text>

                      <HStack
                        justifyContent="space-between"
                        alignItems="center"
                        mt={3}
                      >
                        <HStack alignItems="baseline" space={1}>
                          <Text fontSize="lg" fontWeight="700">
                            ${data.amount ?? 0}
                          </Text>
                          <Text fontSize="xs" color="gray.600">
                            {data.type === "Members"
                              ? Loc.currentLang.feeCollect.perMember
                              : data.type === "Players"
                                ? Loc.currentLang.feeCollect.perPlayer
                                : data.type === "Participants"
                                  ? Loc.currentLang.feeCollect.perParticipant
                                  : data.type === "Both"
                                    ? Loc.currentLang.feeCollect.both
                                    : ""}
                          </Text>
                        </HStack>

                        <HStack alignItems="center" space={1}>
                          <CalendarIcon style={{ color: "#32AE78" }} />
                          <Text fontSize="sm" fontWeight="700">
                            {getDaysLeft(data.end)}
                          </Text>
                        </HStack>
                      </HStack>
                    </VStack>
                  </Box>
                </Pressable>
              );
            })
          )}
        </HStack>
      </Box>

      {showCreateFeeCollection && (
        <AVCreateFeeCollection
          entity={entity}
          showCreateFeeCollection={showCreateFeeCollection}
          setShowCreateFeeCollection={setShowCreateFeeCollection}
        />
      )}
    </>
  );
};

export default AVFeeCollection;
